import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ticketsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { QrCode, Calendar, MapPin, Download, MessageCircle } from 'lucide-react';
import { showToast } from 'shared-ui';

interface Ticket {
  id: string;
  businessId: string;
  ticketNumber: string;
  ticketQRCodeToken: string;
  ticketQRCodeUrl: string;
  ticketQRCodeImage?: string;
  purchasePrice: number;
  purchaseDate: string;
  status: string;
  currentStatus: string;
  event: {
    id: string;
    name: string;
    date: string;
    endDate?: string;
    location?: string;
    coverImage?: string;
  };
  customer: {
    firstName: string;
    lastName: string;
    email: string;
  };
  firstEntryAt?: string;
  lastEntryAt?: string;
  lastExitAt?: string;
  reEntryCount: number;
}

interface WaitingListEntry {
  id: string;
  eventId: string;
  position: number;
  status: string;
  event: {
    name: string;
    date: string;
  };
}

const Tickets: React.FC = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [waitingList, setWaitingList] = useState<WaitingListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    if (user) {
      loadTickets();
      loadWaitingList();
    }
  }, [user]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await ticketsApi.getMyTickets();
      if (response.success && response.data) {
        setTickets(response.data);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWaitingList = async () => {
    try {
      const response = await ticketsApi.getWaitingList();
      if (response.success && response.data) {
        setWaitingList(response.data);
      }
    } catch (error) {
      console.error('Error loading waiting list:', error);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for sold-out event "Join waiting list" (e.g. Events page)
  const handleJoinWaitingList = async (eventId: string) => {
    try {
      const response = await ticketsApi.joinWaitingList(eventId);
      if (response.success) {
        showToast(`You're #${response.data?.position ?? '?'} in the waiting list!`, 'success');
        loadWaitingList();
      }
    } catch (error) {
      console.error('Error joining waiting list:', error);
      showToast('Failed to join waiting list', 'error');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid': return '#28a745';
      case 'used': return '#6c757d';
      case 'inside': return '#17a2b8';
      case 'outside': return '#ffc107';
      case 'departed_early': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusLabel = (ticket: Ticket) => {
    if (ticket.currentStatus === 'inside') return 'Inside Event';
    if (ticket.currentStatus === 'outside') return 'Left Temporarily';
    if (ticket.currentStatus === 'departed_early') return 'Departed Early';
    if (ticket.currentStatus === 'not_entered') return 'Not Entered';
    return ticket.status;
  };

  if (loading) {
    return (
      <div>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>🎫 My Tickets</h1>
        <p style={{ margin: '10px 0 30px 0', color: '#666' }}>Loading your tickets...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>🎫 My Tickets</h1>
      <p style={{ margin: '10px 0 30px 0', color: '#666' }}>Event tickets and passes</p>

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <div style={{ background: 'white', padding: '40px', borderRadius: '12px', textAlign: 'center' }}>
          <span style={{ fontSize: '48px' }}>🎫</span>
          <p style={{ color: '#666', marginTop: '15px' }}>You don't have any tickets yet</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              style={{
                background: 'white',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
                display: 'flex',
                gap: '20px',
                alignItems: 'start'
              }}
            >
              {/* Event Image */}
              {ticket.event.coverImage && (
                <img
                  src={ticket.event.coverImage}
                  alt={ticket.event.name}
                  style={{
                    width: '120px',
                    height: '120px',
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                />
              )}

              {/* Ticket Info */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600' }}>
                      {ticket.event.name}
                    </h3>
                    <div style={{ display: 'flex', gap: '20px', color: '#666', fontSize: '14px', marginBottom: '8px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={16} />
                        {new Date(ticket.event.date).toLocaleDateString('en-AU', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {ticket.event.location && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <MapPin size={16} />
                          {ticket.event.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: getStatusColor(ticket.currentStatus || ticket.status) + '20',
                      color: getStatusColor(ticket.currentStatus || ticket.status)
                    }}
                  >
                    {getStatusLabel(ticket)}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '15px', marginTop: '12px' }}>
                  <button
                    onClick={() => {
                      setSelectedTicket(ticket);
                      setShowQRModal(true);
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <QrCode size={16} />
                    Show QR Code
                  </button>
                  <Link
                    to={`/event-chat/${ticket.event.id}?businessId=${ticket.businessId}`}
                    style={{
                      padding: '10px 20px',
                      background: '#f0f0f0',
                      color: '#333',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <MessageCircle size={16} />
                    Event Chat
                  </Link>
                  {ticket.ticketQRCodeImage && (
                    <a
                      href={ticket.ticketQRCodeImage}
                      download={`ticket-${ticket.ticketNumber}.png`}
                      style={{
                        padding: '10px 20px',
                        background: '#f0f0f0',
                        color: '#333',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Download size={16} />
                      Download
                    </a>
                  )}
                </div>

                <div style={{ marginTop: '12px', fontSize: '12px', color: '#999' }}>
                  Ticket #{ticket.ticketNumber} • ${ticket.purchasePrice.toFixed(2)} AUD
                  {ticket.reEntryCount > 0 && (
                    <span style={{ marginLeft: '10px', color: '#17a2b8' }}>
                      • Re-entered {ticket.reEntryCount}x
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Waiting List */}
      {waitingList.length > 0 && (
        <div style={{ marginTop: '40px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '20px' }}>📋 Waiting List</h2>
          <div style={{ display: 'grid', gap: '15px' }}>
            {waitingList.map((entry) => (
              <div
                key={entry.id}
                style={{
                  background: '#fff3cd',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid #ffc107',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                    {entry.event.name}
                  </h3>
                  <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                    Position #{entry.position} in waiting list
                  </p>
                  <p style={{ margin: '4px 0 0 0', color: '#999', fontSize: '12px' }}>
                    {new Date(entry.event.date).toLocaleDateString('en-AU')}
                  </p>
                </div>
                <span
                  style={{
                    padding: '8px 16px',
                    background: '#ffc107',
                    color: '#333',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {entry.status === 'notified' ? 'Notified!' : 'Waiting...'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedTicket && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowQRModal(false)}
        >
          <div
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 20px 0', fontSize: '24px' }}>Ticket QR Code</h2>
            {selectedTicket.ticketQRCodeImage ? (
              <img
                src={selectedTicket.ticketQRCodeImage}
                alt="Ticket QR Code"
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  margin: '0 auto 20px',
                  display: 'block'
                }}
              />
            ) : (
              <div style={{ padding: '40px', background: '#f0f0f0', borderRadius: '8px', marginBottom: '20px' }}>
                <QrCode size={64} style={{ color: '#999' }} />
                <p style={{ marginTop: '10px', color: '#666' }}>QR Code not available</p>
              </div>
            )}
            <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px' }}>
              Show this QR code at the event entrance
            </p>
            <button
              onClick={() => setShowQRModal(false)}
              style={{
                padding: '12px 24px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
